<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Dynamic Form Styling -->
    <component :is="'style'" v-if="dynamicTextStyles">{{
      dynamicTextStyles
    }}</component>

    <!-- Loading overlay -->
    <div
      v-if="!isBackgroundReady"
      class="fixed inset-0 bg-black z-50 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"
        ></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div
      class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 sm:py-12 lg:px-0 lg:py-0"
    >
      <!-- TOP: Logo (mobile only) + Theme toggle -->
      <div
        class="flex items-center justify-between w-full max-w-sm mx-auto lg:max-w-none lg:justify-end lg:px-12 lg:pt-8 lg:pb-4"
      >
        <!-- Logo (visible only on mobile, hidden during login transition) -->
        <div v-if="!showLoginTransition" class="lg:hidden">
          <img
            :src="logoUrl"
            :class="[
              'h-12 w-auto',
              { 'logo-invert-dark': isDnwaysOrBritech || isGenial },
            ]"
            :alt="nomeEmpresa"
            @touchstart="startLogoPress"
            @touchend="endLogoPress"
            @touchcancel="endLogoPress"
          />
        </div>

        <div class="flex items-center gap-2">
          <!-- Background Customizer (DNWAYS only) -->
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStylingChange"
          />

          <!-- Theme Toggle -->
          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Toggle theme"
            :aria-pressed="isDark"
          >
            <svg
              v-if="!isDark"
              class="w-5 h-5 text-yellow-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-slate-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
              />
            </svg>
          </button>

          <!-- Secret input for editor mode activation (invisible) -->
          <input
            v-if="showSecretInput"
            v-model="secretInput"
            @input="checkSecretCode"
            ref="secretInput"
            type="text"
            class="fixed -left-full opacity-0 w-0 h-0"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- CENTER: Main content -->
      <div
        class="flex-1 flex flex-col justify-center w-full"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >
        <!-- ======== TYPING/LANDING SCREEN STATE ======== -->
        <div
          v-if="showLandingScreen && !openToken"
          class="form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo com backdrop discreto - desktop only -->
          <div class="hidden lg:flex lg:justify-center mb-4 lg:mb-5">
            <div
              :class="[
                'relative inline-flex items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <img
                :src="logoUrl"
                :class="[
                  'h-28 w-auto',
                  { 'logo-invert-dark': shouldInvertLogo },
                ]"
                :alt="nomeEmpresa"
              />
            </div>
          </div>

          <!-- Typing animation messages -->
          <div class="space-y-6 mb-8 lg:mb-10">
            <!-- Primeira mensagem -->
            <div class="text-center">
              <p
                class="text-xl lg:text-2xl text-white font-light tracking-wide"
              >
                {{ typedText1 }}
              </p>
            </div>

            <!-- Segunda mensagem (aparece depois da primeira) -->
            <div class="text-center">
              <p class="text-base lg:text-xl text-white/80">
                {{ typedText2 }}
              </p>
            </div>
          </div>

          <!-- Botões (aparecem após as mensagens no mobile, sempre visíveis no desktop) -->
          <div
            v-if="showMainContent"
            class="space-y-4 lg:flex lg:justify-center lg:space-y-0 lg:gap-5 transition-all duration-500"
          >
            <!-- Botão Criar conta (apenas se configurado) -->
            <div v-if="possuiBotaoCadastroTopo" class="relative w-full lg:w-60">
              <Button
                @click="showTipoDropdown = !showTipoDropdown"
                label="Criar conta"
                class="w-full btn-dynamic-color"
              />
              <!-- Dropdown de tipos -->
              <div
                v-if="showTipoDropdown"
                class="absolute top-full mt-2 w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-lg overflow-hidden z-50"
              >
                <button
                  v-for="tipo in tiposClientes"
                  :key="tipo.value"
                  @click="navigateToCadastro(tipo.value)"
                  class="w-full px-4 py-3 text-left text-white hover:bg-white/20 transition-colors font-medium"
                >
                  {{ tipo.label }}
                </button>
              </div>
            </div>
            <button
              @click="startLoginTransition"
              class="w-full lg:w-60 h-12 rounded-xl border border-white/30 text-white font-semibold text-base hover:bg-white/10 transition-colors"
            >
              Fazer login
            </button>
          </div>
        </div>

        <!-- ======== LOGIN TRANSITION + FORM ======== -->
        <div
          v-if="showLoginTransition && !openToken"
          class="form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo centralizada -->
          <div class="flex justify-center mb-10">
            <div
              :class="[
                'relative inline-flex items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <img
                :src="logoUrl"
                :class="[
                  'h-12 lg:h-24 w-auto',
                  { 'logo-invert-dark': shouldInvertLogo },
                ]"
                :alt="nomeEmpresa"
              />
            </div>
          </div>

          <!-- Mensagens digitando -->
          <div class="space-y-6 mb-8">
            <!-- "Acesse sua conta" -->
            <div v-if="!isGenial" class="flex justify-center">
              <div
                :class="[
                  'inline-flex px-3 py-1.5 rounded-xl',
                  { 'bg-black/10': !isFormStylingActive },
                ]"
              >
                <h2
                  class="text-2xl sm:text-3xl font-bold text-white text-center"
                >
                  {{ loginTypedText1 }}
                </h2>
              </div>
            </div>

            <!-- "Insira seus dados para continuar." -->
            <div v-if="showLoginSecondMessage" class="flex justify-center">
              <div class="inline-flex px-3 py-1 rounded-lg">
                <p class="text-sm text-white/60 text-center">
                  {{ loginTypedText2 }}
                </p>
              </div>
            </div>
          </div>

          <!-- Formulário (aparece após mensagens) -->
          <div v-if="showLoginForm">
            <!-- Voltar para landing (só se cadastro habilitado) -->
            <button
              v-if="possuiBotaoCadastroTopo"
              @click="resetToLanding"
              class="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Voltar
            </button>

            <form @submit.prevent="sendLogin" class="space-y-5">
              <!-- Email/CPF/CNPJ -->
              <div class="relative">
                <label class="block text-sm font-medium mb-2">
                  {{ labelCampoLogin }}
                </label>
                <InputText
                  v-model="loginSession.Usuario"
                  :class="{ 'border-red-500': erro }"
                  @input="onEmailInput"
                  @blur="handleEmailBlur"
                  @click="clear"
                  ref="emailField"
                  autocomplete="off"
                  placeholder="Digite aqui"
                />
                <ul
                  v-if="showSuggestions && loginSession.Usuario"
                  class="absolute z-50 w-full bg-slate-800/95 backdrop-blur-md border border-white/20 rounded-xl mt-1 shadow-lg max-h-40 overflow-y-auto"
                >
                  <li
                    v-for="suggestion in filteredSuggestions"
                    :key="suggestion"
                    @mousedown.prevent="applySuggestion(suggestion)"
                    class="px-4 py-3 hover:bg-white/10 cursor-pointer text-white text-sm"
                  >
                    {{ loginSession.Usuario + suggestion }}
                  </li>
                </ul>
              </div>

              <!-- Apelido (quando configurado e >1 opção) -->
              <div
                v-if="
                  configuracaoApelido === 'S' &&
                  exibirCampoApelido &&
                  apelidos &&
                  apelidos.length > 1
                "
              >
                <label class="block text-sm font-medium mb-2">
                  Selecione a conta para acesso
                </label>
                <Select
                  v-model="loginSession.Apelido"
                  :options="apelidos"
                  optionLabel="Apelido"
                  :disabled="
                    isApelidoDisabled || !apelidos || apelidos.length === 0
                  "
                  :placeholder="
                    apelidos && apelidos.length > 0
                      ? 'Selecione a conta'
                      : 'Nenhuma conta disponível'
                  "
                  class="w-full"
                  :class="{ 'p-invalid': erro }"
                  @change="onApelidoChange"
                >
                  <template #option="slotProps">
                    <div class="flex items-center justify-between w-full">
                      <span>{{ slotProps.option.Apelido }}</span>
                      <span
                        v-if="temMultiplosTipos"
                        class="text-xs opacity-60 ml-2"
                        >{{ slotProps.option.TipoContaDescricao }}</span
                      >
                    </div>
                  </template>
                  <template #value="slotProps">
                    <span v-if="slotProps.value">
                      {{ slotProps.value.Apelido }}
                      <span
                        v-if="temMultiplosTipos"
                        class="text-xs opacity-60 ml-1"
                        >({{ slotProps.value.TipoContaDescricao }})</span
                      >
                    </span>
                    <span v-else>{{ slotProps.placeholder }}</span>
                  </template>
                </Select>
              </div>

              <!-- Senha -->
              <div v-if="modoAutenticacao !== 2">
                <label class="block text-sm font-medium mb-2"> Senha </label>
                <Password
                  v-model="loginSession.Senha"
                  :class="{ 'p-invalid': erro }"
                  inputClass="w-full"
                  :feedback="false"
                  toggleMask
                  @click="clear"
                  autocomplete="off"
                />
              </div>

              <!-- Reenviar Email Ativação / Sem Acesso (modais) -->
              <ModalAvisoReenvioEmail @reenviar="reenviarEmail" />
              <ModalAvisoSemAcesso @enviar-link="enviarLinkDefinirSenha" />

              <!-- Botão Entrar/Avançar -->
              <Button
                type="submit"
                :loading="disableBtnLogin"
                :disabled="disableBtnLogin"
                :label="modoAutenticacao === 2 ? 'Avançar' : 'Entrar'"
                class="w-full btn-dynamic-color"
              />

              <!-- Link Esqueci senha -->
              <div v-if="modoAutenticacao !== 2" class="text-center pt-1">
                <router-link
                  to="/RecuperarSenha"
                  class="text-sm font-medium text-white/60 hover:text-white transition-colors"
                >
                  Esqueci minha senha
                </router-link>
              </div>
            </form>
          </div>
        </div>

        <!-- ======== TOKEN STATE ======== -->
        <div
          v-if="openToken"
          class="space-y-6 form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Voltar -->
          <button
            @click="voltar()"
            class="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar
          </button>

          <!-- Token Icon -->
          <div class="flex justify-center">
            <div
              :class="[
                'w-16 h-16 rounded-full flex items-center justify-center',
                { 'bg-white/10': !isFormStylingActive },
              ]"
            >
              <svg
                class="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          <!-- Título Token -->
          <div class="flex justify-center">
            <div
              :class="[
                'inline-flex px-3 py-1.5 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <h2 class="text-2xl sm:text-3xl font-bold text-white text-center">
                {{ customTokenTitle }}
              </h2>
            </div>
          </div>

          <!-- Descrição Token -->
          <div class="flex justify-center">
            <div class="inline-flex px-3 py-1 rounded-lg">
              <p class="text-sm text-white/60 text-center">
                {{ customTokenSubtitle }}
              </p>
            </div>
          </div>

          <!-- Email Provider Link -->
          <div v-if="emailProviderInfo" class="flex justify-center">
            <a
              :href="gmailMobileLink || emailProviderInfo.link"
              target="_blank"
              class="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <img
                :src="emailProviderInfo.icon"
                :alt="emailProviderInfo.name"
                class="w-5 h-5"
              />
              <span class="text-sm font-medium"
                >Abrir {{ emailProviderInfo.name }}</span
              >
            </a>
          </div>

          <!-- Formulário Token -->
          <form @submit.prevent="sendLoginWithToken" class="space-y-6">
            <div class="flex justify-center gap-2">
              <input
                v-for="n in 6"
                :key="n"
                :ref="'tokenInput' + (n - 1)"
                type="text"
                inputmode="numeric"
                maxlength="1"
                v-model="tokenBoxes[n - 1]"
                @input="onTokenInput($event, n - 1)"
                @keydown.backspace="onTokenBackspace($event, n - 1)"
                @paste="n === 1 ? onTokenPaste($event) : null"
                :class="{ 'border-red-500': erro }"
                class="w-12 h-14 text-center text-2xl rounded-xl border border-white/20 bg-white/10 text-white focus:ring-2 focus:ring-white/20 focus:border-white/40 transition-colors backdrop-blur-sm"
                autocomplete="one-time-code"
              />
            </div>

            <Button
              type="submit"
              :loading="disableBtnLogin"
              :disabled="disableBtnLogin"
              label="Avançar"
              class="w-full btn-dynamic-color"
            />

            <!-- Reenviar Token -->
            <div class="text-center space-y-2">
              <button
                type="button"
                @click="reenviarToken"
                :disabled="!reenviarTokenDisponivel"
                class="text-sm font-medium text-white/60 hover:text-white hover:underline disabled:text-white/30 disabled:no-underline disabled:cursor-not-allowed transition-colors"
              >
                Reenviar token
              </button>
              <p v-if="!reenviarTokenDisponivel" class="text-xs text-white/40">
                Disponível em {{ reenviarTokenTimer }} segundos
              </p>
            </div>
          </form>
        </div>
      </div>

      <!-- BOTTOM: Versão -->
      <!-- <div class="w-full max-w-sm mx-auto lg:max-w-2xl text-center pt-6 lg:pb-8">
        <p class="text-xs text-white/30">
          Versão {{ versaoSistema }}
        </p>
      </div> -->
    </div>
  </div>

  <!-- Modais -->
  <ModalAvisoLogin
    :mensagem="messageError"
    :mensagensComplementares="mensagensComplementares"
  />
  <ModalAvisoPersonalizadoGenial
    v-if="nomeEmpresa === 'GENIAL' && configuracaoModalAprovacaoPersonalizada"
  />

  <!-- PWA iOS Install Tip (only for DNWAYS) -->
  <div v-if="showIOSInstallTip" class="ios-install-tip">
    <button
      type="button"
      class="ios-install-tip__close"
      @click="closeIOSInstallTip"
      aria-label="Fechar dica de instalação"
    >
      ×
    </button>

    <strong class="ios-install-tip__title">
      Instale este app no seu iPhone
    </strong>

    <p class="ios-install-tip__text">
      Toque no ícone de Compartilhar
      <span class="ios-install-tip__share-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path
            d="M12 16V4m0 0L8 8m4-4 4 4M6 10v9h12v-9"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      e escolha "Adicionar à Tela de Início".
    </p>

    <span class="ios-install-tip__arrow"></span>
  </div>

  <!-- PWA Android Install Button -->
  <button
    v-if="showAndroidInstallButton && !showIOSInstallTip && isDnwaysOrBritech"
    type="button"
    class="pwa-install-button"
    @click="installApp"
  >
    Instalar app
  </button>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import api from "@/api/axios";
import ModalAvisoLogin from "./Login/ModalAvisoLogin.vue";
import ModalAvisoReenvioEmail from "./Login/ModalAvisoReenvioEmail.vue";
import ModalAvisoSemAcesso from "./Login/ModalAvisoSemAcesso.vue";
import ModalAvisoPersonalizadoGenial from "./Login/ModalMensagemPersonalizadaAprovacao.vue";
import BackgroundCustomizer from "@/components/BackgroundCustomizer.vue";
import SHA256 from "crypto-js/sha256";
import { useTheme } from "@/composables/useTheme";
import { useEditorMode } from "@/composables/useEditorMode";

// Constantes para validação
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CNPJ_REGEX =
  /^([A-Z0-9]{2}\.?[A-Z0-9]{3}\.?[A-Z0-9]{3}\/?[A-Z0-9]{4}-?\d{2})$/;
const CPF_REGEX = /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$/;

export default {
  name: "UserLogin",
  components: {
    ModalAvisoLogin,
    ModalAvisoReenvioEmail,
    ModalAvisoSemAcesso,
    ModalAvisoPersonalizadoGenial,
    BackgroundCustomizer,
  },
  setup() {
    const { isDark, toggleTheme } = useTheme();
    const { editorModeActive, toggleEditorMode, initEditorMode } =
      useEditorMode();

    // Inicializa editor mode do localStorage
    initEditorMode();

    return {
      isDark,
      toggleTheme,
      editorModeActive,
      toggleEditorMode,
    };
  },
  data() {
    return {
      tab: 1,
      loginSession: {
        Usuario: null,
        Senha: null,
        Token: null,
        Hash: null,
        Apelido: null,
      },
      showSuggestions: false,
      emailSuggestions: [
        "gmail.com",
        "outlook.com",
        "hotmail.com",
        "yahoo.com",
      ],
      filteredSuggestions: [],
      tokenBoxes: Array(6).fill(""),
      apelidos: [],
      usuarioVerificado: false,
      disableBtnLogin: false,
      messageError: null,
      mensagensComplementares: false,
      erro: false,
      openToken: false,
      clienteGuidReenvioEmail: "",
      nomeEmpresa: "",
      loading: false,
      tiposClientes: [],
      configuracaoTiposClientes: {},
      configuracaoDuplaAutenticacao: false,
      configuracaoModalAprovacaoPersonalizada: false,
      configuracaoURLEntrar: "",
      configuracaoApelido: "",
      isApelidoDisabled: false,
      exibirCampoApelido: false,
      isMobile: false,
      modoAutenticacao: 0,
      showLandingScreen: true,
      reenviarTokenDisponivel: false,
      reenviarTokenTimer: 30,
      reenviarTokenInterval: null,
      // Typing animation (landing)
      showTypingWelcome: false,
      typedText1: "",
      typedText2: "",
      welcomeMessage1: "",
      welcomeMessage2: "Cadastre-se ou acesse sua conta para continuar.",
      showMainContent: false,
      showSecondMessage: false,
      // Login transition animation
      showLoginTransition: false,
      loginTypedText1: "",
      loginTypedText2: "",
      loginMessage1: "",
      loginMessage2: "",
      showLoginSecondMessage: false,
      showLoginForm: false,
      // Cadastro tipo selection
      showTipoDropdown: false,
      // Background customization
      customBackgroundStyle: null,
      customButtonColor: null,
      isBackgroundReady: false,
      // Form styling customization
      formStylingConfig: null,
      // Mobile logo press to reveal customizer
      showBackgroundCustomizer: false,
      // Secret editor mode activation
      themeClickCount: 0,
      themeClickTimer: null,
      secretInput: "",
      showSecretInput: false,
      logoPressTimer: null,
      // PWA Install states
      deferredPrompt: null,
      showAndroidInstallButton: false,
      showIOSInstallTip: false,
    };
  },
  computed: {
    isTypingAnimationDisabled() {
      return true;
    },
    logoUrl() {
      return require("@/" + process.env.VUE_APP_URL_LOGO);
    },
    gmailMobileLink() {
      if (!this.openToken || !this.emailProviderInfo) return null;
      const isGmail = this.emailProviderInfo.name === "Gmail";
      if (!isGmail) return null;
      // Detecta se é mobile
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const isAndroid = /android/i.test(ua);
      const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      if (isAndroid) {
        return "intent://#Intent;package=com.google.android.gm;scheme=mailto;end";
      }
      if (isIOS) {
        return "googlegmail://";
      }
      return null;
    },
    emailProviderInfo() {
      const email = this.loginSession?.Usuario || "";
      const match = email.match(/@([\w.-]+)$/);
      if (!match) return null;
      const domain = match[1].toLowerCase();
      if (domain === "gmail.com" || domain === "dnways.com") {
        return {
          name: "Gmail",
          link: "https://mail.google.com/",
          icon: require("@/assets/gmail.svg"), // coloque o ícone na pasta assets
        };
      }
      if (
        domain === "outlook.com" ||
        domain === "hotmail.com" ||
        domain === "live.com" ||
        domain === "live.co.uk"
      ) {
        return {
          name: "Outlook",
          link: "https://outlook.live.com/",
          icon: require("@/assets/outlook.svg"), // coloque o ícone na pasta assets
        };
      }
      return null;
    },
    ...mapGetters({
      getConfiguracoes: "GET_CONFIGURACOES",
    }),
    labelCampoLogin() {
      // Label dinâmica baseada em tipos permitidos
      const tiposPermitidos = this.tiposClientes.map((t) => parseInt(t.value));
      const temPF = tiposPermitidos.includes(1);
      const temPJ = tiposPermitidos.includes(2);
      const temFundo = tiposPermitidos.includes(3);

      if (temPF && (temPJ || temFundo)) {
        return "E-mail, CPF ou CNPJ";
      } else if (temPF) {
        return "E-mail ou CPF";
      } else if (temPJ && !temPF && !temFundo) {
        // Apenas PJ (TipoCliente 2)
        return "E-mail da empresa ou CNPJ";
      } else if (temPJ || temFundo) {
        return "E-mail ou CNPJ";
      }
      return "E-mail";
    },
    versaoSistema() {
      return process.env.APP_VERSION ?? "";
    },
    mensagemLoginHeader() {
      return process.env.VUE_APP_MENSAGEM_LOGIN_HEADER;
    },
    possuiLogoEsquerdaTopo() {
      return process.env.VUE_APP_POSSUI_LOGO_ESQUERDA == 1 ? true : false;
    },
    possuiBotaoCadastroTopo() {
      return process.env.VUE_APP_POSSUI_BOTAO_CADASTRAR_TOPO == 1
        ? true
        : false;
    },
    centralizarLogin() {
      return process.env.VUE_APP_CENTRALIZAR_LOGIN == 1 ? true : false;
    },
    // Propriedades computadas para melhorar legibilidade
    isGenial() {
      return this.nomeEmpresa === "GENIAL";
    },
    isRJI() {
      return this.nomeEmpresa === "RJI";
    },
    isActual() {
      return this.nomeEmpresa === "ACTUAL";
    },
    isDnwaysOrBritech() {
      return (
        this.nomeEmpresa === "DNWAYS" ||
        this.nomeEmpresa === "DNWAYS_HML" ||
        this.nomeEmpresa === "DNWAYS_DEMO_Y" ||
        this.nomeEmpresa === "DNWAYS_DEMO_Y_HML" ||
        this.nomeEmpresa === "DNWAYS_DEMO_X" ||
        this.nomeEmpresa === "DNWAYS_DEMO_X_HML"
      );
    },
    isVortex() {
      return this.nomeEmpresa === "Vortex";
    },
    isLastro() {
      return this.nomeEmpresa === "Lastro";
    },
    showApelidoField() {
      return this.configuracaoApelido === "S";
    },
    apelidoParaLogin() {
      const a = this.loginSession.Apelido;
      return a && typeof a === "object" ? a.Apelido : a;
    },
    temMultiplosTipos() {
      const tipos = [...new Set(this.apelidos.map((a) => a.TipoConta ?? a))];
      return tipos.length > 1;
    },
    activeEmailField() {
      return this.tab === 1
        ? "emailField1"
        : this.tab === 2
        ? "emailField2"
        : "emailField3";
    },
    useDuploFator() {
      return this.configuracaoDuplaAutenticacao;
    },
    detectedDevice() {
      const width = window.innerWidth;
      if (width <= 768) return "mobile";
      if (width <= 1024) return "tablet";
      return "desktop";
    },
    currentFormConfig() {
      if (!this.formStylingConfig) return null;
      return this.formStylingConfig[this.detectedDevice];
    },
    isFormStylingActive() {
      return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
    },
    formContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { backgroundColor, opacity, width, minWidth, maxWidth, margin } =
        this.currentFormConfig;
      return {
        backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
        backdropFilter: "blur(16px)",
        borderRadius: "1rem",
        padding: "2rem",
        width: width || "100%",
        minWidth: minWidth || "0px",
        maxWidth: maxWidth || "600px",
        marginLeft: margin || "0rem",
        marginRight: margin || "0rem",
      };
    },
    formContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const { alignment } = this.currentFormConfig;
      return alignment === "left"
        ? "mr-auto"
        : alignment === "right"
        ? "ml-auto"
        : "mx-auto";
    },
    parentContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "items-center mx-auto";
      }
      const { alignment } = this.currentFormConfig;
      if (alignment === "left") {
        return "items-start";
      } else if (alignment === "right") {
        return "items-end";
      } else {
        return "items-center mx-auto";
      }
    },
    parentContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return {};
      }
      const { alignment, alignmentOffset } = this.currentFormConfig;
      const style = {};

      // Apply padding based on alignment and offset
      if (alignment === "left") {
        style.paddingLeft = `${alignmentOffset * 0.25}rem`;
      } else if (alignment === "right") {
        style.paddingRight = `${alignmentOffset * 0.25}rem`;
      }

      return style;
    },
    childContainerWidthClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "w-full";
      }
      // When form styling is active, don't force full width - let maxWidth control it
      return "";
    },
    shouldInvertLogo() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return this.isDnwaysOrBritech || this.isGenial;
      }
      return this.currentFormConfig.logoInvert;
    },
    customPreLoginTitle() {
      if (
        this.isFormStylingActive &&
        this.currentFormConfig?.customTexts?.preLoginTitle
      ) {
        const value = this.currentFormConfig.customTexts.preLoginTitle;
        // Aceita tanto string quanto objeto {enabled, content}
        if (typeof value === "string") return value;
        if (value.enabled && value.content) return value.content;
      }
      return `Olá, seja bem-vindo a ${this.nomeEmpresa}.`;
    },
    customPreLoginSubtitle() {
      if (
        this.isFormStylingActive &&
        this.currentFormConfig?.customTexts?.preLoginSubtitle
      ) {
        const value = this.currentFormConfig.customTexts.preLoginSubtitle;
        if (typeof value === "string") return value;
        if (value.enabled && value.content) return value.content;
      }
      return "Cadastre-se ou acesse sua conta para continuar.";
    },
    customFormTitle() {
      if (
        this.isFormStylingActive &&
        this.currentFormConfig?.customTexts?.formTitle
      ) {
        const value = this.currentFormConfig.customTexts.formTitle;
        if (typeof value === "string") return value;
        if (value.enabled && value.content) return value.content;
      }
      return "Acesse sua conta";
    },
    customFormSubtitle() {
      if (
        this.isFormStylingActive &&
        this.currentFormConfig?.customTexts?.formSubtitle
      ) {
        const value = this.currentFormConfig.customTexts.formSubtitle;
        if (typeof value === "string") {
          return value;
        }
        if (value.enabled && value.content) {
          return value.content;
        }
      }
      return "Insira seus dados para continuar.";
    },
    customTokenTitle() {
      if (
        this.isFormStylingActive &&
        this.currentFormConfig?.customTexts?.tokenTitle
      ) {
        const value = this.currentFormConfig.customTexts.tokenTitle;
        if (typeof value === "string") return value;
        if (value.enabled && value.content) return value.content;
      }
      return "Chave de acesso";
    },
    customTokenSubtitle() {
      if (
        this.isFormStylingActive &&
        this.currentFormConfig?.customTexts?.tokenSubtitle
      ) {
        const value = this.currentFormConfig.customTexts.tokenSubtitle;
        if (typeof value === "string") return value;
        if (value.enabled && value.content) return value.content;
      }
      return "Para continuar, digite o código que acabamos de enviar para o seu e-mail.";
    },
    dynamicTextStyles() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const config = this.currentFormConfig;
      return `
        .form-container h1, .form-container h2, .form-container h3,
        .form-container h4, .form-container h5, .form-container h6,
        .form-container span, .form-container label, .form-container p {
          color: ${config.textColor} !important;
        }
        .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
          background-color: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container label,
        .login-glass .form-container p {
          color: ${config.textColor} !important;
          background: none !important;
        }
        .login-glass .form-container .p-inputtext:enabled,
        .login-glass .form-container input.p-inputtext:enabled,
        .login-glass .form-container input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-dropdown:enabled,
        .login-glass .form-container select:enabled,
        .login-glass .form-container textarea:enabled {
          background: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext:disabled,
        .login-glass .form-container input.p-inputtext:disabled,
        .login-glass .form-container input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-dropdown:disabled,
        .login-glass .form-container select:disabled,
        .login-glass .form-container textarea:disabled {
          background: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container .p-inputtext,
        .login-glass .form-container input.p-inputtext,
        .login-glass .form-container .p-password input {
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext::placeholder,
        .login-glass .form-container input.p-inputtext::placeholder,
        .login-glass .form-container .p-password input::placeholder {
          color: ${config.inputTextColor} !important;
          opacity: 0.6 !important;
        }
        .form-container a,
        .form-container ~ div a,
        .form-container ~ .text-center a {
          color: ${config.textColor} !important;
        }
        .form-container button:not([type="submit"]),
        .form-container ~ button:not([type="submit"]),
        button.flex.items-center {
          color: ${config.textColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full,
        .form-container button.btn-dynamic-color,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined),
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) .p-button-label,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) span {
          color: ${config.buttonTextColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full:not(.border),
        .form-container button.btn-dynamic-color,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
          background-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
          border-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
        }
        .form-container button[type="submit"]:hover,
        .form-container button.w-full:not(.border):hover,
        .form-container button.btn-dynamic-color:hover,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .p-button:not(.p-button-text):not(.p-button-outlined):hover .p-button-label {
          color: ${config.buttonHoverColor} !important;
        }
        button.w-full.border,
        button.h-12.border,
        .landing-cta button.border {
          background-color: ${this.hexToRgba(
            config.buttonColorType2,
            config.buttonOpacityType2 / 100,
          )} !important;
          border-color: ${config.buttonColorType2} !important;
        }
      `;
    },
  },
  async created() {
    await this.fetchConfiguracao([
      "Remover tipo de cadastro",
      "Modo de autenticação",
      "Modal de aprovação personalizada",
      "Redirecionamento do botão entrar Onboarding externo",
      "Exibir apelido",
    ]);
    this.configuracaoTiposClientes = this.getConfiguracoes(
      "Remover tipo de cadastro",
    );
    this.modoAutenticacao =
      parseInt(this.getConfiguracoes("Modo de autenticação")?.Valor) || 0;
    this.configuracaoDuplaAutenticacao =
      this.getConfiguracoes("Modo de autenticação")?.Valor != "0"
        ? true
        : false;
    this.configuracaoModalAprovacaoPersonalizada =
      this.getConfiguracoes("Modal de aprovação personalizada")?.Valor == "S"
        ? true
        : false;
    this.configuracaoURLEntrar = this.getConfiguracoes(
      "Redirecionamento do botão entrar Onboarding externo",
    ).Valor;
    this.configuracaoApelido = this.getConfiguracoes("Exibir apelido").Valor;
  },
  mounted() {
    // Verifica se editor mode já está ativo (persiste entre refreshes)
    if (this.editorModeActive) {
      this.showBackgroundCustomizer = true;
    }

    this.disableBtnLogin = false;
    this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
    this.isMobile = window.innerWidth <= 768;
    this.tab = this.nomeEmpresa != "GENIAL" ? 1 : 2;

    // Se não tem botão de cadastro no topo (GENIAL/RJI), vai direto para login
    if (this.possuiBotaoCadastroTopo) {
      this.showLandingScreen = true;
      // Inicia animação de boas-vindas apenas no mobile
      this.checkIfMobileForTyping();
    } else {
      // Pula landing screen, vai direto para formulário de login
      this.showLandingScreen = false;
      this.startLoginTransition();
    }

    this.$store.commit("SET_MODAL_AVISO_APROVACAO_PERSONALIZADA", false);
    this.$store.commit("SET_MODAL_AVISO_LOGIN", false);

    // PWA: Listen for install prompt event (Android/Chrome)
    window.addEventListener(
      "beforeinstallprompt",
      this.handleBeforeInstallPrompt,
    );

    // PWA: Show iOS install tip if applicable (only for DNWAYS clients)
    const isDnwaysClient =
      this.nomeEmpresa === "DNWAYS" ||
      this.nomeEmpresa === "DNWAYS_HML" ||
      this.nomeEmpresa === "DNWAYS_DEMO_Y" ||
      this.nomeEmpresa === "DNWAYS_DEMO_Y_HML" ||
      this.nomeEmpresa === "DNWAYS_DEMO_X" ||
      this.nomeEmpresa === "DNWAYS_DEMO_X_HML";
    if (isDnwaysClient) {
      const hasDismissedIOSInstallTip =
        localStorage.getItem("ios-install-tip-dismissed") === "true";
      if (this.isIOS() && !this.isStandalone() && !hasDismissedIOSInstallTip) {
        this.showIOSInstallTip = true;
      }
    }
  },
  beforeUnmount() {
    // PWA: Remove install prompt listener
    window.removeEventListener(
      "beforeinstallprompt",
      this.handleBeforeInstallPrompt,
    );

    // Limpa timers se existirem
    if (this.logoPressTimer) {
      clearTimeout(this.logoPressTimer);
    }
    if (this.reenviarTokenInterval) {
      clearInterval(this.reenviarTokenInterval);
    }
    if (this.themeClickTimer) {
      clearTimeout(this.themeClickTimer);
    }
  },
  methods: {
    ...mapActions({
      signIn: "auth/signIn",
      verifyLogin: "auth/verifyLogin",
      verifyStatusRegister: "auth/verifyStatusRegister",
      fetchConfiguracao: "FETCH_CONFIGURACOES",
    }),
    handleBackgroundChange(style) {
      this.customBackgroundStyle = style;
      this.isBackgroundReady = true;
    },
    handleButtonColorChange(color) {
      this.customButtonColor = color;
    },
    handleFormStylingChange(config) {
      // Sincroniza o ícone do tema com o modo do backend na primeira carga
      if (!this.formStylingConfig && config) {
        const backendMode = config[this.detectedDevice]?.mode;
        if (backendMode) {
          const shouldBeDark = backendMode === "dark";
          if (this.isDark !== shouldBeDark) {
            this.isDark = shouldBeDark;
            localStorage.setItem("theme", backendMode);
            if (shouldBeDark) {
              document.documentElement.classList.add("dark");
            } else {
              document.documentElement.classList.remove("dark");
            }
          }
        }
      }

      this.formStylingConfig = config;
    },
    hexToRgba(hex, alpha = 1) {
      hex = hex.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    handleThemeClick() {
      // Toggle theme normalmente
      this.toggleTheme();

      // Sincroniza o modo interno do form styling com o tema visual
      if (this.$refs.backgroundCustomizer) {
        const newMode = this.isDark ? "dark" : "light";
        this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
      }

      // Detecta triple-click para ativar modo secreto
      this.themeClickCount++;

      // Reseta contador após 2 segundos
      if (this.themeClickTimer) {
        clearTimeout(this.themeClickTimer);
      }

      this.themeClickTimer = setTimeout(() => {
        this.themeClickCount = 0;
      }, 2000);

      // Após 3 cliques, mostra input secreto
      if (this.themeClickCount === 3) {
        this.showSecretInput = true;
        this.secretInput = "";
        this.themeClickCount = 0;

        // Auto-focus no input invisível
        this.$nextTick(() => {
          if (this.$refs.secretInput) {
            this.$refs.secretInput.focus();
          }
        });

        // Esconde input após 5 segundos se não digitar nada
        setTimeout(() => {
          if (this.secretInput === "") {
            this.showSecretInput = false;
          }
        }, 5000);
      }
    },
    checkSecretCode() {
      const input = this.secretInput.toLowerCase();

      // Verifica se digitou 'editor'
      if (input === "editor") {
        this.toggleEditorMode();
        this.showBackgroundCustomizer = this.editorModeActive;
        this.showSecretInput = false;
        this.secretInput = "";

        // Feedback visual discreto
      }

      // Esconde input se digitou algo errado com mais de 6 caracteres
      if (input.length > 6 && input !== "editor") {
        this.showSecretInput = false;
        this.secretInput = "";
      }
    },
    startLogoPress() {
      // Apenas no mobile e para empresas DNWAYS
      if (window.innerWidth >= 1024) {
        // Desktop - sempre mostra
        this.showBackgroundCustomizer = true;
        return;
      }

      // const isDnways = this.nomeEmpresa === 'DNWAYS' ||
      //                  this.nomeEmpresa === 'DNWAYS_DEMO_Y' ||
      //                  this.nomeEmpresa === 'DNWAYS_DEMO_X';

      const isDnways = true;

      if (!isDnways) return;

      // Mobile - requer 5 segundos de press
      this.logoPressTimer = setTimeout(() => {
        this.showBackgroundCustomizer = true;
      }, 5000);
    },
    endLogoPress() {
      if (this.logoPressTimer) {
        clearTimeout(this.logoPressTimer);
        this.logoPressTimer = null;
      }
    },
    checkIfMobileForTyping() {
      // Define a primeira mensagem com o nome da empresa
      this.welcomeMessage1 = this.customPreLoginTitle;
      this.welcomeMessage2 = this.customPreLoginSubtitle;

      // Mostra o texto direto sem animação
      this.typedText1 = this.customPreLoginTitle;
      this.typedText2 = this.customPreLoginSubtitle;
      this.showSecondMessage = true;
      this.showMainContent = true;
    },
    startTypingAnimation() {
      // Previne múltiplas chamadas - se já está digitando, não inicia de novo
      if (this.typedText1.length > 0 || this.typedText2.length > 0) {
        return;
      }

      // IMPORTANTE: Reseta os textos antes de começar (casotenham sido definidos pelos watchers)
      this.typedText1 = "";
      this.typedText2 = "";
      this.showSecondMessage = false;

      const typingSpeed = 60; // ms por caractere
      const pauseBetweenMessages = 600; // pausa entre mensagens
      const pauseBeforeButtons = 500; // pausa antes de mostrar botões

      // Digita a primeira mensagem
      let index1 = 0;
      const typeFirstMessage = () => {
        if (index1 < this.welcomeMessage1.length) {
          this.typedText1 += this.welcomeMessage1[index1];
          index1++;
          setTimeout(typeFirstMessage, typingSpeed);
        } else {
          // Primeira mensagem completa, pausa e começa a segunda
          setTimeout(() => {
            this.showSecondMessage = true;
            typeSecondMessage();
          }, pauseBetweenMessages);
        }
      };

      // Digita a segunda mensagem
      let index2 = 0;
      const typeSecondMessage = () => {
        if (index2 < this.welcomeMessage2.length) {
          const char = this.welcomeMessage2[index2];
          this.typedText2 += char;
          index2++;
          setTimeout(typeSecondMessage, typingSpeed);
        } else {
          // Segunda mensagem completa, mostra botões (mantendo mensagens)
          setTimeout(() => {
            this.showMainContent = true;
          }, pauseBeforeButtons);
        }
      };

      // Pequeno delay antes de começar a digitar a primeira mensagem
      setTimeout(typeFirstMessage, 500);
    },
    resetToLanding() {
      // Reseta todos os estados para voltar à landing
      this.showLoginForm = false;
      this.showLoginTransition = false;
      this.showLandingScreen = true;
      this.loginTypedText1 = "";
      this.loginTypedText2 = "";
      this.showLoginSecondMessage = false;
    },
    navigateToCadastro(tipoValue) {
      this.showTipoDropdown = false;
      this.$router.push({
        path: "/CadastroInicio",
        query: { tipo: tipoValue },
      });
    },
    startLoginTransition() {
      this.showLandingScreen = false;
      this.showLoginTransition = true;
      this.startLoginTypingAnimation();
    },
    startLoginTypingAnimation() {
      // Define as mensagens com textos customizados
      this.loginMessage1 = this.customFormTitle;
      this.loginMessage2 = this.customFormSubtitle;

      // IMPORTANTE: Reseta os textos antes de começar
      this.loginTypedText1 = "";
      this.loginTypedText2 = "";
      this.showLoginSecondMessage = false;

      // Verifica se a animação está desabilitada via env
      // Mostra tudo imediatamente sem animação
      this.loginTypedText1 = this.loginMessage1;
      this.loginTypedText2 = this.loginMessage2;
      this.showLoginSecondMessage = true;
      this.showLoginForm = true;
      return;
    },
    onTokenPaste(e) {
      const pasted = (e.clipboardData || window.clipboardData).getData("text");
      if (pasted && pasted.length === 6 && /^\d{6}$/.test(pasted)) {
        for (let i = 0; i < 6; i++) {
          this.tokenBoxes[i] = pasted[i];
        }
        // Move o foco para o último input
        this.$nextTick(() => {
          this.$refs["tokenInput5"][0].focus();
        });
        e.preventDefault();

        setTimeout(() => {
          this.sendLoginWithToken();
        }, 100);
      }
    },
    onTokenInput(e, idx) {
      const value = e.target.value.replace(/\D/g, "");
      this.tokenBoxes[idx] = value;
      if (value && idx < 5) {
        this.$refs["tokenInput" + (idx + 1)][0].focus();
      } else if (idx == 5 && value) {
        this.sendLoginWithToken();
      }
    },
    onTokenBackspace(e, idx) {
      if (!this.tokenBoxes[idx] && idx > 0) {
        this.$refs["tokenInput" + (idx - 1)][0].focus();
      }
    },
    avisoLogin() {
      this.$store.commit("SET_MODAL_AVISO_LOGIN", true);
    },
    avisoLoginAprovadoPersonalizado() {
      this.$store.commit("SET_MODAL_AVISO_APROVACAO_PERSONALIZADA", true);
    },
    reenviarToken() {
      this.reenviarTokenDisponivel = false;
      this.reenviarTokenTimer = 30;
      if (this.reenviarTokenInterval) clearInterval(this.reenviarTokenInterval);
      this.reenviarTokenInterval = setInterval(() => {
        if (this.reenviarTokenTimer > 0) {
          this.reenviarTokenTimer--;
        }
        if (this.reenviarTokenTimer === 0) {
          this.reenviarTokenDisponivel = true;
          clearInterval(this.reenviarTokenInterval);
          this.reenviarTokenInterval = null;
        }
      }, 1000);
      this.sendLogin();
      this.$alert({
        icon: "success",
        title: "Token enviado!",
        text: "Acesse o e-mail cadastrado para visualizar o e-mail.",
      });
    },
    voltar() {
      this.resetLoginSession();
      this.openToken = false;
    },
    resetLoginSession() {
      this.loginSession = {
        Usuario: null,
        Senha: null,
        Token: null,
        Hash: null,
        Apelido: null,
      };
    },
    // PWA Methods
    isIOS() {
      return (
        /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    },
    isStandalone() {
      return (
        window.navigator.standalone === true ||
        window.matchMedia("(display-mode: standalone)").matches
      );
    },
    handleBeforeInstallPrompt(event) {
      event.preventDefault();
      this.deferredPrompt = event;
      this.showAndroidInstallButton = true;
    },
    async installApp() {
      if (!this.deferredPrompt) return;

      this.deferredPrompt.prompt();

      const choice = await this.deferredPrompt.userChoice;

      if (choice.outcome === "accepted") {
        this.showAndroidInstallButton = false;
      }

      this.deferredPrompt = null;
    },
    closeIOSInstallTip() {
      this.showIOSInstallTip = false;
      localStorage.setItem("ios-install-tip-dismissed", "true");
    },
    /**
     * Limpa mensagens de erro
     */
    clear() {
      this.messageError = "";
      this.erro = false;
    },
    async validarPreAutenticacaoModoLoginToken() {
      var model = {
        Usuario: this.loginSession.Usuario,
        Apelido: this.apelidoParaLogin,
        TipoCliente: this.tab,
      };
      await this.verifyStatusRegister(model)
        .then((response) => {
          this.disableBtnLogin = false;
          if (
            response.data?.data?.User.StatusCadastro == 2 &&
            this.nomeEmpresa == "GENIAL"
          ) {
            this.avisoLoginAprovadoPersonalizado();
          } else if (
            response.data.data.User.StatusCadastro == 0 ||
            (response.data.data.User.StatusCadastro == 2 &&
              this.nomeEmpresa != "GENIAL") ||
            (response.data.data.User.StatusCadastro == 3 &&
              this.nomeEmpresa != "GENIAL") ||
            (response.data.data.User.StatusCadastro == 4 &&
              this.nomeEmpresa != "GENIAL")
          ) {
            this.messageError = "";
            this.erro = false;
            this.openToken = true;
          }
        })
        .catch((error) => {
          const msgErro = error?.response?.data?.data?.erros?.[0];
          if (
            msgErro === "MODAL_APROVACAO_PERSONALIZADA." &&
            this.nomeEmpresa == "GENIAL"
          ) {
            this.disableBtnLogin = false;
            this.avisoLoginAprovadoPersonalizado();
            return;
          }

          this.tratamentoErroLogin(error);
        });
    },
    verificarTipoCliente(tipo) {
      return this.tiposClientes.some(
        (cliente) => Number(cliente.value) === Number(tipo),
      );
    },
    selectTab(index) {
      // Método mantido para compatibilidade, mas não é mais usado (tabs removidas)
      this.tab = index;
    },
    onApelidoChange() {
      const apelido = this.loginSession.Apelido;
      if (apelido && typeof apelido === "object" && apelido.TipoConta) {
        this.tab = apelido.TipoConta;
      }
    },
    applySuggestion(suffix) {
      this.loginSession.Usuario += suffix;
      this.showSuggestions = false;
      // Sempre chamar handleEmailBlur para detectar tipo
      this.handleEmailBlur();
    },
    onEmailInput() {
      // Resetar verificação quando o usuário digita
      this.usuarioVerificado = false;
      this.apelidos = [];
      this.loginSession.Apelido = null;
      this.exibirCampoApelido = false;

      const val = this.loginSession.Usuario;
      const onlyNumbers = val.replace(/\D/g, "");
      const alphanumeric = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

      // Obter tipos permitidos
      const tiposPermitidos = this.tiposClientes.map((t) => parseInt(t.value));
      const temPF = tiposPermitidos.includes(1);
      const temPJouFundo =
        tiposPermitidos.includes(2) || tiposPermitidos.includes(3);

      // Se for só número, aplica máscara baseada em tipos permitidos
      if (
        !val.includes("@") &&
        (/^\d+$/.test(alphanumeric) ||
          (/^[A-Z0-9]+$/.test(alphanumeric) &&
            alphanumeric.length >= 12 &&
            onlyNumbers.length >= 1 &&
            !val.includes("@")))
      ) {
        // Lógica de detecção progressiva:
        // 1-11 dígitos: CPF (se permitido)
        // 12+ dígitos: CNPJ (se permitido)

        if (/^\d+$/.test(alphanumeric) && onlyNumbers.length <= 11) {
          // Até 11 dígitos: tenta CPF se permitido
          if (temPF) {
            this.loginSession.Usuario = this.cpfMask(onlyNumbers);
            // Se completou 11 dígitos e é CPF válido
            if (onlyNumbers.length === 11 && this.isValidCPF(onlyNumbers)) {
              this.handleEmailBlur();
              this.focusNextField();
            }
          } else if (temPJouFundo) {
            // Se PF não permitido mas PJ/Fundo sim, já aplica CNPJ desde o início
            this.loginSession.Usuario = this.cnpjMask(onlyNumbers);
          } else {
            // Nenhum tipo permitido, mantém números
            this.loginSession.Usuario = onlyNumbers;
          }
        } else if (alphanumeric.length <= 14) {
          // 12-14 dígitos: transição para CNPJ
          if (temPJouFundo) {
            this.loginSession.Usuario = this.cnpjMask(alphanumeric);
            // Se completou 14 dígitos e é CNPJ válido
            if (alphanumeric.length === 14 && this.isValidCNPJ(alphanumeric)) {
              this.handleEmailBlur();
              this.focusNextField();
            }
          } else {
            // CNPJ não permitido, mantém números
            this.loginSession.Usuario = onlyNumbers;
          }
        } else {
          // Mais de 14 dígitos: limita a 14 e aplica CNPJ
          const limitedNumbers = alphanumeric.substring(0, 14);
          if (temPJouFundo) {
            this.loginSession.Usuario = this.cnpjMask(limitedNumbers);
          } else {
            this.loginSession.Usuario = limitedNumbers;
          }
        }
      } else {
        // Se tem letras, mantém valor original (email)
        this.loginSession.Usuario = val;
      }

      // Regex: contém pelo menos uma letra
      const contemLetra = /[a-zA-Z]/.test(val);

      const temArrobaComCaractereDepois = /@[^\s@]+/.test(val);

      if (
        contemLetra &&
        val.length >= 3 &&
        val.includes("@") &&
        !temArrobaComCaractereDepois
      ) {
        this.filteredSuggestions = this.emailSuggestions;
        this.showSuggestions = true;
      } else {
        this.showSuggestions = false;
      }
    },
    focusNextField() {
      // Seleciona todos os campos interativos visíveis e habilitados do formulário
      const selectors =
        'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])';
      // Se quiser limitar ao formulário atual:
      // const form = this.$el.closest('form');
      // const fields = Array.from(form.querySelectorAll(selectors));
      // Se quiser considerar toda a tela:
      const fields = Array.from(document.querySelectorAll(selectors));
      const currentIndex = fields.findIndex(
        (field) => field === document.activeElement,
      );
      if (currentIndex !== -1 && currentIndex + 1 < fields.length) {
        fields[currentIndex + 1].focus();
      }
    },
    cnpjMask(value) {
      // value deve ser só números!
      value = String(value)
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase()
        .substring(0, 14);

      if (value.length <= 2) return value;
      if (value.length <= 5)
        return value.replace(/^([A-Z0-9]{2})([A-Z0-9]+)/, "$1.$2");
      if (value.length <= 8)
        return value.replace(
          /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]+)/,
          "$1.$2.$3",
        );
      if (value.length <= 12)
        return value.replace(
          /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]+)/,
          "$1.$2.$3/$4",
        );
      return value.replace(
        /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{0,2}).*/,
        "$1.$2.$3/$4-$5",
      );
    },
    cpfMask(value) {
      // Remove tudo que não for número
      value = value.replace(/\D/g, "");
      // Aplica a máscara
      if (value.length <= 3) return value;
      if (value.length <= 6) return value.replace(/^(\d{3})(\d+)/, "$1.$2");
      if (value.length <= 9)
        return value.replace(/^(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
      return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, "$1.$2.$3-$4");
    },
    isValidCPF(cpf) {
      cpf = cpf.replace(/\D/g, "");
      if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
      let sum = 0,
        rest;
      for (let i = 1; i <= 9; i++)
        sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
      rest = (sum * 10) % 11;
      if (rest === 10 || rest === 11) rest = 0;
      if (rest !== parseInt(cpf.substring(9, 10))) return false;
      sum = 0;
      for (let i = 1; i <= 10; i++)
        sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
      rest = (sum * 10) % 11;
      if (rest === 10 || rest === 11) rest = 0;
      return rest === parseInt(cpf.substring(10, 11));
    },
    isValidCNPJ(cnpj) {
      cnpj = String(cnpj)
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase();
      if (cnpj.length !== 14) return false;
      if (/^([A-Z0-9])\1+$/.test(cnpj)) return false;
      if (!/^\d{2}$/.test(cnpj.substring(12))) return false;

      const valorChar = (ch) => ch.charCodeAt(0) - 48;
      const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

      let soma1 = 0;
      for (let i = 0; i < 12; i++) {
        soma1 += valorChar(cnpj.charAt(i)) * pesos1[i];
      }
      const dv1 = soma1 % 11 < 2 ? 0 : 11 - (soma1 % 11);
      if (dv1 !== parseInt(cnpj.charAt(12), 10)) return false;

      let soma2 = 0;
      for (let j = 0; j < 13; j++) {
        soma2 += valorChar(cnpj.charAt(j)) * pesos2[j];
      }
      const dv2 = soma2 % 11 < 2 ? 0 : 11 - (soma2 % 11);
      return dv2 === parseInt(cnpj.charAt(13), 10);
    },
    async handleEmailBlur() {
      if (!this.loginSession.Usuario) {
        this.apelidos = [];
        this.exibirCampoApelido = false;
        this.usuarioVerificado = false;
        return;
      }

      this.loading = true;
      this.erro = false;
      this.messageError = null;

      try {
        // Tentar novo endpoint primeiro
        const response = await api.post("/login/verificar-usuario", {
          chaveLogin: this.loginSession.Usuario,
        });

        // Backend retorna em PascalCase dentro de data
        const resultado = response.data.data;

        if (resultado && resultado.Sucesso) {
          // Usuário encontrado
          this.tab = resultado.TipoCliente;
          this.apelidos = resultado.Apelidos || [];
          this.usuarioVerificado = true;

          // Auto-seleciona apelido se houver apenas 1
          if (this.apelidos.length === 1 && this.configuracaoApelido === "S") {
            this.loginSession.Apelido = this.apelidos[0];
            if (this.apelidos[0].TipoConta)
              this.tab = this.apelidos[0].TipoConta;
            this.exibirCampoApelido = false;
          } else if (
            this.apelidos.length > 1 &&
            this.configuracaoApelido === "S"
          ) {
            // Mostra dropdown apenas se >1 apelido
            this.loginSession.Apelido = null;
            this.exibirCampoApelido = true;
          } else {
            this.exibirCampoApelido = false;
          }
        } else {
          // Usuário não encontrado - não mostra erro no blur
          // O erro será tratado no submit
          this.apelidos = [];
          this.exibirCampoApelido = false;
          this.usuarioVerificado = false;
        }
      } catch (error) {
        // Erros de rede (ERR_CONNECTION_REFUSED, timeout, etc) ou 404
        // Fazem fallback silencioso para método antigo
        if (!error.response || error.response?.status === 404) {
          // Sem resposta do servidor (erro de rede) ou endpoint não existe
          await this.handleEmailBlurFallback();
        } else {
          // Outros erros HTTP (500, 400, etc) - não bloqueia, apenas limpa
          this.apelidos = [];
          this.exibirCampoApelido = false;
          this.usuarioVerificado = false;
        }
      } finally {
        this.loading = false;
        // Em paralelo: detecta estado de acesso (pendente / sem acesso) via VerifyLogin
        this.verificarEstadoAcesso();
      }
    },
    async verificarEstadoAcesso() {
      if (!this.loginSession.Usuario) return;
      try {
        const response = await this.verifyLogin({
          Usuario: this.loginSession.Usuario,
          Apelido: this.apelidoParaLogin,
          TipoCliente: this.tab,
          Senha: "",
        });
        const data = response?.data?.data;
        if (!data?.Sucesso) {
          this.aplicarEstadoAcesso(data?.Mensagem, data?.clienteGuid);
        }
      } catch (error) {
        const erroPrincipal =
          error?.response?.data?.data?.erros?.[0] ??
          error?.response?.data?.data?.Mensagem;
        const guid = error?.response?.data?.data?.clienteGuid ?? "";
        this.aplicarEstadoAcesso(erroPrincipal, guid);
      }
    },
    aplicarEstadoAcesso(mensagem, guid) {
      if (!mensagem) return;
      if (
        mensagem === "Usuário com e-mail não validado!" ||
        mensagem === "Cliente com e-mail não validado!"
      ) {
        this.clienteGuidReenvioEmail = guid;
        this.$store.commit("SET_MODAL_REENVIO_EMAIL", true);
      } else if (mensagem === "Usuário sem acesso!") {
        this.clienteGuidReenvioEmail = guid;
        this.$store.commit("SET_MODAL_SEM_ACESSO", true);
      }
      // outros erros (cliente não encontrado, etc.) caem no ModalAvisoLogin genérico
    },
    async enviarLinkDefinirSenha() {
      if (!this.loginSession.Usuario) return;
      try {
        this.disableBtnLogin = true;
        await api.post("/register/restaurar-senha", {
          Email: this.loginSession.Usuario,
          ClienteGuid: this.clienteGuidReenvioEmail,
        });
        this.$alert({
          icon: "success",
          title: "Link enviado!",
          text: "Acesse seu e-mail para definir uma senha.",
        });
      } catch (error) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: error?.response?.data?.descricao ?? "Falha ao enviar o link.",
        });
      } finally {
        this.disableBtnLogin = false;
        this.$store.commit("SET_MODAL_SEM_ACESSO", false);
      }
    },

    async handleEmailBlurFallback() {
      // Método antigo: tenta tipos sequencialmente
      const input = this.loginSession.Usuario;
      const tiposPermitidos = this.tiposClientes.map((t) => parseInt(t.value));

      // Detectar padrão
      const onlyNumbers = input.replace(/\D/g, "");
      const alphanumeric = input.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

      if (onlyNumbers.length === 11) {
        // CPF - tipo 1 (PF)
        if (tiposPermitidos.includes(1)) {
          this.tab = 1;
          // PF não precisa buscar apelidos
          this.apelidos = [];
          this.exibirCampoApelido = false;
        }
      } else if (alphanumeric.length === 14) {
        // CNPJ - tipo 2 (PJ) ou 3 (Fundo)
        const tiposCNPJ = [2, 3].filter((t) => tiposPermitidos.includes(t));

        for (let tipo of tiposCNPJ) {
          this.tab = tipo;
          try {
            if (this.configuracaoApelido === "S") {
              const response = await api.get("/login", {
                params: {
                  chaveLogin: input,
                  tipoCliente: tipo,
                },
              });
              this.apelidos = response.data.data || [];

              if (this.apelidos.length === 1) {
                this.loginSession.Apelido = this.apelidos[0];
                this.exibirCampoApelido = false;
              } else if (this.apelidos.length > 1) {
                this.loginSession.Apelido = null;
                this.exibirCampoApelido = true;
              }
            }
            return; // Encontrou
          } catch (error) {
            continue; // Tenta próximo tipo
          }
        }
      } else if (input.includes("@")) {
        // Email - tenta tipos permitidos
        for (let tipo of tiposPermitidos) {
          this.tab = tipo;
          try {
            if (this.configuracaoApelido === "S" && tipo !== 1) {
              const response = await api.get("/login", {
                params: {
                  chaveLogin: input,
                  tipoCliente: tipo,
                },
              });
              this.apelidos = response.data.data || [];

              if (this.apelidos.length === 1) {
                this.loginSession.Apelido = this.apelidos[0];
                this.exibirCampoApelido = false;
              } else if (this.apelidos.length > 1) {
                this.loginSession.Apelido = null;
                this.exibirCampoApelido = true;
              }
            } else {
              this.apelidos = [];
              this.exibirCampoApelido = false;
            }
            return; // Encontrou
          } catch (error) {
            continue; // Tenta próximo tipo
          }
        }
      }
    },
    irParaCadastroInicial() {
      this.$router.push({ name: "CadastroInicio" });
    },
    async reenviarEmail() {
      if (this.loginSession.Usuario == "") {
        this.messageError = "Verifique os dados";
        this.erro = true;
        return;
      }
      var model = {
        Email: this.loginSession.Usuario,
        ClienteGuid: this.clienteGuidReenvioEmail,
      };
      await api
        .post("/register/email-ativar-conta", model)
        .then((retorno) => {
          if (!retorno.data) {
            this.$alert({
              icon: "error",
              title: "Oops...",
              text: `Falha no envio do e-mail. Verifique as credenciais de acesso.`,
            });
          } else {
            this.messageError = "";
            this.erro = false;
            this.$alert({
              icon: "success",
              title: "E-mail enviado!",
              text: "Acesse o e-mail cadastrado para confirmar seu acesso.",
            }).then((result) => {
              if (result.value) {
                this.$router.push({ name: "UserLogin" });
              }
            });
          }
        })
        .catch((error) => {
          this.disableBtn = false;
          this.$alert({
            icon: "error",
            title: "Oops...",
            text: `${error.response.data.descricao}`,
          });
        });
    },
    async sendLoginWithToken() {
      this.disableBtnLogin = true;

      var model = {
        Usuario: this.loginSession.Usuario,
        Senha: this.loginSession.Senha
          ? SHA256(this.loginSession.Senha).toString()
          : "",
        Token: this.loginSession.Token,
        Apelido: this.apelidoParaLogin,
        TipoCliente: this.tab,
        HashToken: this.loginSession.Hash,
      };

      await this.signIn(model)
        .then((response) => {
          this.disableBtnLogin = false;
          if (response.status == 200) {
            this.messageError = "";
            this.erro = false;
            this.$router.replace({
              path: "/",
            });
          }
        })
        .catch(() => {
          this.disableBtnLogin = false;
          this.erro = true;

          this.$alert({
            icon: "error",
            title: "Oops...",
            text: `O Token informado não é válido! Verifique e tente novamente.`,
          });
        });
    },
    exibirErro(mensagem, exibirModal = true) {
      this.disableBtnLogin = false;
      this.messageError =
        mensagem ||
        "Ocorreu um erro desconhecido. Entre em contato com o suporte.";
      this.erro = true;
      if (exibirModal) {
        this.avisoLogin();
      }
      return false;
    },
    validarEntrada() {
      if (!this.loginSession.Usuario) {
        this.exibirErro(`Preencha o campo: ${this.labelCampoLogin}`);
        return false;
      }

      if (this.modoAutenticacao == 2) {
        const usuario = this.loginSession.Usuario;
        const onlyNumbers = usuario.replace(/\D/g, "");
        const alphanumeric = usuario.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

        // Valida formato baseado no que foi digitado
        const isEmail = EMAIL_REGEX.test(usuario);
        const isCPF = onlyNumbers.length === 11 && CPF_REGEX.test(usuario);
        const isCNPJ =
          alphanumeric.length === 14 && CNPJ_REGEX.test(usuario.toUpperCase());

        if (!isEmail && !isCPF && !isCNPJ) {
          this.messageError = "Verifique os dados";
          this.erro = true;
          this.disableBtnLogin = false;
          this.exibirErro(
            "Formato inválido! Digite um e-mail, CPF ou CNPJ válido.",
          );
          return false;
        }
      }

      if (
        (this.loginSession.Senha == "" || this.loginSession.Senha == null) &&
        this.modoAutenticacao != 2
      ) {
        this.messageError = "Preencha a senha.";
        this.avisoLogin();
        this.erro = true;
        this.disableBtnLogin = false;
        return false;
      } else if (
        this.configuracaoApelido == "S" &&
        this.exibirCampoApelido &&
        (this.loginSession.Apelido == "" || this.loginSession.Apelido == null)
      ) {
        this.messageError = "Selecione um apelido da conta";
        this.avisoLogin();
        this.erro = true;
        this.disableBtnLogin = false;
        return false;
      }

      return true;
    },
    tratamentoErroLogin(error) {
      this.disableBtnLogin = false;
      var erroPrincipal = error.response.data?.data?.erros
        ? error.response.data?.data?.erros[0]
        : error.response.data.data[0];

      if (
        erroPrincipal == "Cliente com e-mail não validado!" ||
        erroPrincipal == "Usuário com e-mail não validado!"
      ) {
        // Capturar ClienteGuid para reenvio de e-mail
        this.clienteGuidReenvioEmail =
          error.response.data?.data?.clienteGuid || "";
        this.$store.commit("SET_MODAL_REENVIO_EMAIL", true);
        this.erro = true;
        this.disableBtnLogin = false;
        return;
      }

      if (erroPrincipal == "Usuário sem acesso!") {
        this.clienteGuidReenvioEmail =
          error.response.data?.data?.clienteGuid || "";
        this.$store.commit("SET_MODAL_SEM_ACESSO", true);
        this.erro = true;
        this.disableBtnLogin = false;
        return;
      }

      if (erroPrincipal == "Cliente não encontrado!") {
        if (this.isGenial) {
          // Enviar dados para CadastroInicio
          const usuario = this.loginSession.Usuario;
          const alphanumeric = usuario
            .replace(/[^A-Za-z0-9]/g, "")
            .toUpperCase();
          const isCNPJ = alphanumeric.length === 14;
          const isEmail = usuario.includes("@");

          this.$router.push({
            name: "CadastroInicio",
            query: {
              email: isEmail ? usuario : undefined,
              cnpj: isCNPJ ? usuario : undefined,
            },
          });
        } else {
          this.mensagensComplementares = true;
        }
      }

      if (
        erroPrincipal == "Usuário está em processo de análise." &&
        this.isGenial
      ) {
        this.$alert({
          icon: "warning",
          title: "Cadastro enviado para análise!",
          text: "Estamos analisando os dados recebidos. Em breve você receberá novidades no e-mail informado.",
        });
        return;
      }

      this.messageError = erroPrincipal;
      this.avisoLogin();
      this.erro = true;
    },
    async validarPreAutenticacaoModoLoginSenhaToken(model) {
      await this.verifyLogin(model)
        .then((response) => {
          this.disableBtnLogin = false;
          if (response.data.data.Sucesso) {
            this.messageError = "";
            this.erro = false;
            this.openToken = true;
          }
        })
        .catch((error) => {
          this.tratamentoErroLogin(error);
        });
    },
    async validarAutenticacaoSimples(model) {
      await this.signIn(model)
        .then((response) => {
          this.disableBtnLogin = false;
          if (response.status == 200) {
            this.messageError = "";
            this.erro = false;
            this.$router.replace({
              path: "/",
            });
          }
        })
        .catch((error) => {
          this.tratamentoErroLogin(error);
        });
    },
    async sendLogin() {
      this.disableBtnLogin = true;
      this.erro = false;
      this.mensagensComplementares = false;
      this.$store.commit("SET_MODAL_REENVIO_EMAIL", false);
      this.$store.commit("SET_MODAL_SEM_ACESSO", false);

      if (!this.validarEntrada()) {
        return;
      }

      // VALIDAÇÃO OBRIGATÓRIA: Verificar usuário antes de fazer login
      if (!this.usuarioVerificado) {
        await this.handleEmailBlur();

        // Se após verificar ainda não está verificado, há erro
        if (!this.usuarioVerificado) {
          // GENIAL: redireciona para cadastro se usuário não encontrado
          if (this.isGenial) {
            const usuario = this.loginSession.Usuario;
            const alphanumeric = usuario
              .replace(/[^A-Za-z0-9]/g, "")
              .toUpperCase();
            const isCNPJ = alphanumeric.length === 14;
            const isEmail = usuario.includes("@");

            this.$router.push({
              name: "CadastroInicio",
              query: {
                email: isEmail ? usuario : undefined,
                cnpj: isCNPJ ? usuario : undefined,
              },
            });
            return;
          }

          // Outras empresas: mostra erro
          this.messageError =
            "Não foi possível verificar o usuário. Tente novamente.";
          this.erro = true;
          this.disableBtnLogin = false;
          this.avisoLogin();
          return;
        }
      }

      if (this.modoAutenticacao == 2) {
        this.validarPreAutenticacaoModoLoginToken();
        return;
      }

      var model = {
        Usuario: this.loginSession.Usuario,
        Senha: this.loginSession.Senha
          ? SHA256(this.loginSession.Senha).toString()
          : "",
        Apelido: this.apelidoParaLogin,
        tipoCliente: this.tab,
      };

      if (this.configuracaoDuplaAutenticacao) {
        this.validarPreAutenticacaoModoLoginSenhaToken(model);
      } else {
        this.validarAutenticacaoSimples(model);
      }
    },
  },
  watch: {
    customPreLoginTitle(newVal) {
      // Atualiza apenas welcomeMessage - a animação usa isso como fonte
      if (this.welcomeMessage1 !== newVal) {
        this.welcomeMessage1 = newVal;
      }
    },
    customPreLoginSubtitle(newVal) {
      if (this.welcomeMessage2 !== newVal) {
        this.welcomeMessage2 = newVal;
      }
    },
    customFormTitle(newVal) {
      if (this.loginMessage1 !== newVal) {
        this.loginMessage1 = newVal;
      }
    },
    customFormSubtitle(newVal) {
      if (this.loginMessage2 !== newVal) {
        this.loginMessage2 = newVal;

        // Se o formulário de login já está visível ou animação desabilitada, atualiza texto diretamente
        if (
          this.showLoginForm ||
          this.showLoginSecondMessage ||
          this.isTypingAnimationDisabled
        ) {
          this.loginTypedText2 = newVal;
        }
      }
    },
    tokenBoxes: {
      handler(val) {
        this.loginSession.Token = val.join("");
      },
      deep: true,
    },
    openToken(newVal) {
      if (newVal) {
        this.reenviarTokenDisponivel = false;
        this.reenviarTokenTimer = 30;
        if (this.reenviarTokenInterval)
          clearInterval(this.reenviarTokenInterval);
        this.reenviarTokenInterval = setInterval(() => {
          if (this.reenviarTokenTimer > 0) {
            this.reenviarTokenTimer--;
          }
          if (this.reenviarTokenTimer === 0) {
            this.reenviarTokenDisponivel = true;
            clearInterval(this.reenviarTokenInterval);
            this.reenviarTokenInterval = null;
          }
        }, 1000);

        this.tokenBoxes = Array(6).fill("");
      } else {
        // Limpa timer se voltar para tela anterior
        if (this.reenviarTokenInterval) {
          clearInterval(this.reenviarTokenInterval);
          this.reenviarTokenInterval = null;
        }
        this.reenviarTokenDisponivel = false;
        this.reenviarTokenTimer = 30;
      }
    },
    configuracaoTiposClientes(newVal) {
      if (newVal && newVal.Valor) {
        const valores = newVal.Valor.replace("\r\n", ";")
          .replace(",", ";")
          .split(";")
          .map((item) => item.trim())
          .filter((item) => item);
        const todasOpcoes = [
          { value: "1", label: "Pessoa Física" },
          { value: "2", label: "Pessoa Jurídica" },
          { value: "3", label: "Fundo de Investimento" },
          { value: "4", label: "Institucional" },
        ];
        const tiposFiltrados = todasOpcoes.filter(
          (opcao) => !valores.includes(opcao.value),
        );
        if (
          JSON.stringify(tiposFiltrados) !== JSON.stringify(this.tiposClientes)
        ) {
          this.tiposClientes = tiposFiltrados;
        }
      } else {
        if (this.tiposClientes.length > 0) {
          this.tiposClientes = [];
        }
      }
      if (this.tiposClientes.length > 0) {
        this.tab = parseInt(this.tiposClientes[0].value);
      }
    },
  },
};
</script>

<style scoped>
/* PWA iOS Install Tip */
.ios-install-tip {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 9999;

  background: rgba(255, 255, 255, 0.96);
  color: #0f255f;

  border-radius: 18px;
  padding: 18px 20px 22px;

  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  text-align: left;

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.ios-install-tip__title {
  display: block;
  padding-right: 32px;
  margin-bottom: 10px;

  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.ios-install-tip__text {
  margin: 0;

  font-size: 15px;
  line-height: 1.5;
}

.ios-install-tip__close {
  position: absolute;
  top: 10px;
  right: 14px;

  border: 0;
  background: transparent;

  color: #0f255f;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.ios-install-tip__share-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;
  margin: 0 4px;

  vertical-align: middle;

  border-radius: 8px;
  background: #f0f0f0;
  color: #007aff;

  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.ios-install-tip__arrow {
  position: absolute;
  left: 50%;
  bottom: -11px;

  width: 22px;
  height: 22px;

  background: rgba(255, 255, 255, 0.96);
  transform: translateX(-50%) rotate(45deg);

  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.08);
}

/* PWA Android Install Button */
.pwa-install-button {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 24px;
  z-index: 9999;

  max-width: 400px;
  margin: 0 auto;
  width: 100%;

  height: 52px;

  border: 0;
  border-radius: 14px;

  background: #0f255f;
  color: #ffffff;

  font-size: 16px;
  font-weight: 700;

  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  cursor: pointer;
}

.pwa-install-button:hover {
  background: #1a3a7f;
}

@media (min-width: 600px) {
  .pwa-install-button {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
}
</style>
